import React from 'react';
import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import TinyMCEEditor from './TinyMCEEditor';

const QUESTION_TYPES = [
    { label: 'Single Selection', value: 'single' },
    { label: 'True/False', value: 'truefalse' },
    { label: 'Multiple Choice', value: 'multiple' },
];

const QUESTION_LEVELS = [
    { label: 'Easy', value: 'easy' },
    { label: 'Medium', value: 'medium' },
    { label: 'Hard', value: 'hard' },
];

const MEDIUM = [
    { label: 'English', value: 'english' },
    { label: 'Hindi', value: 'hindi' },
];

const validationSchema = Yup.object({
    description: Yup.string().required('Question description is required'),
    questionType: Yup.string().required('Question type is required'),
    questionLevel: Yup.string().required('Question level is required'),
    medium: Yup.string().required('Medium is required'),
    answers: Yup.array()
        .of(
            Yup.object({
                text: Yup.string().required('Option text is required'),
                isCorrect: Yup.boolean().required(),
            })
        )
        .min(1, 'At least one answer is required'),
    correctAnswerIndex: Yup.number().when('questionType', {
        is: (value) => ['single', 'truefalse'].includes(value),
        then: (schema) =>
            schema
                .integer()
                .min(0, 'Select a correct answer')
                .required('Select a correct answer'),
        otherwise: (schema) => schema.notRequired(),
    }),
});

const EditQuestionModal = ({ question, handleSubmit, closeModal }) => {
    console.log("Question received in EditQuestionModal:", question); // Debug
    const initialValues = {
        description: question?.description || '',
        questionType: question?.questionType || '',
        questionLevel: question?.questionLevel || '',
        medium: question?.medium || '',
        answers: question?.answers || [],
        correctAnswerIndex: question?.correctAnswerIndex ?? -1,
    };

    const handleTypeChange = (setFieldValue, value) => {
        console.log('Question type changed to:', value); // Debug
        setFieldValue('questionType', value);
        if (value === 'truefalse') {
            setFieldValue('answers', [
                { text: 'True', isCorrect: false },
                { text: 'False', isCorrect: false },
            ]);
            setFieldValue('correctAnswerIndex', -1);
        } else if (value === 'single') {
            setFieldValue('answers', Array(4).fill().map(() => ({ text: '', isCorrect: false })));
            setFieldValue('correctAnswerIndex', -1);
        } else {
            setFieldValue('answers', []);
            setFieldValue('correctAnswerIndex', -1);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            console.log('Enter key prevented from submitting form'); // Debug
        }
    };

    // Custom component to integrate TinyMCE with Formik
    const TinyMCEField = ({ field, form }) => {
        const { name, value } = field;
        const { setFieldValue, setFieldTouched } = form;
        // console.log(value)

        return (
            <TinyMCEEditor
                value={value}
                onChange={(content) => {
                    console.log('TinyMCE content changed:', content); // Debug
                    setFieldValue(name, content);
                    setFieldTouched(name, true);
                }}
                aria-label="Question description with rich text and images"
            />
        );
    };

    return (
        <div className="modal fade" id="edit_question">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="fw-bold">Edit Question</h5>
                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            onClick={closeModal}
                            aria-label="Close modal"
                        />
                    </div>
                    <div className="modal-body">
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            enableReinitialize
                            validateOnChange
                            validateOnBlur
                            onSubmit={async (values, { resetForm, setTouched }) => {
                                try {
                                    console.log('Form submitted with values:', values); // Debug
                                    // Set all fields as touched to show errors
                                    setTouched({
                                        description: true,
                                        questionType: true,
                                        questionLevel: true,
                                        medium: true,
                                        answers: values.answers.map(() => ({ text: true, isCorrect: true })),
                                        correctAnswerIndex: true,
                                    });

                                    // Validate form manually
                                    await validationSchema.validate(values, { abortEarly: false });

                                    // Update answers with correctAnswerIndex
                                    const updatedAnswers = values.answers.map((ans, idx) => ({
                                        ...ans,
                                        isCorrect:
                                            values.questionType === 'single' || values.questionType === 'truefalse'
                                                ? idx === Number(values.correctAnswerIndex)
                                                : ans.isCorrect,
                                    }));

                                    handleSubmit({ ...values, answers: updatedAnswers }, question._id, { resetForm });
                                    closeModal('edit_question');
                                    resetForm();
                                } catch (error) {
                                    console.error('Validation failed:', error.errors); // Debug
                                }
                            }}
                        >
                            {({ values, setFieldValue, touched, errors, isValid, isSubmitting }) => (
                                <Form id="edit-form" onKeyDown={handleKeyDown}>
                                    <div className="mb-3">
                                        <label className="form-label" htmlFor="description">
                                            Question Description
                                        </label>
                                        <Field
                                            name="description"
                                            component={TinyMCEField}
                                            aria-label="Question description with rich text and images"
                                        />
                                        {touched.description && errors.description && (
                                            <div className="text-danger">{errors.description}</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label" htmlFor="medium">
                                            Medium
                                        </label>
                                        <Field
                                            as="select"
                                            name="medium"
                                            id="medium"
                                            className="form-select"
                                            aria-label="Select medium"
                                        >
                                            <option value="">Select Medium</option>
                                            {MEDIUM.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </Field>
                                        {touched.medium && errors.medium && (
                                            <div className="text-danger">{errors.medium}</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label" htmlFor="questionType">
                                            Question Type
                                        </label>
                                        <Field
                                            as="select"
                                            name="questionType"
                                            id="questionType"
                                            className="form-select"
                                            onChange={(e) => handleTypeChange(setFieldValue, e.target.value)}
                                            aria-label="Select question type"
                                        >
                                            <option value="">Select Type</option>
                                            {QUESTION_TYPES.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </Field>
                                        {touched.questionType && errors.questionType && (
                                            <div className="text-danger">{errors.questionType}</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label" htmlFor="questionLevel">
                                            Question Level
                                        </label>
                                        <Field
                                            as="select"
                                            name="questionLevel"
                                            id="questionLevel"
                                            className="form-select"
                                            aria-label="Select question level"
                                        >
                                            <option value="">Select Level</option>
                                            {QUESTION_LEVELS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </Field>
                                        {touched.questionLevel && errors.questionLevel && (
                                            <div className="text-danger">{errors.questionLevel}</div>
                                        )}
                                    </div>

                                    <h6>Answers</h6>
                                    <FieldArray name="answers">
                                        {({ push, remove }) => (
                                            <div>
                                                {values.answers.length > 0 ? (
                                                    values.answers.map((answer, index) => (
                                                        <div key={index}>
                                                            <div className="d-flex mb-2 align-items-center">
                                                                <Field
                                                                    type="text"
                                                                    name={`answers[${index}].text`}
                                                                    className="form-control me-2"
                                                                    placeholder={`Option ${index + 1}`}
                                                                    aria-label={`Option ${index + 1}`}
                                                                />
                                                                <div className="form-check">
                                                                    <Field
                                                                        type={
                                                                            values.questionType === 'single' || values.questionType === 'truefalse'
                                                                                ? 'radio'
                                                                                : 'checkbox'
                                                                        }
                                                                        name={
                                                                            values.questionType === 'single' || values.questionType === 'truefalse'
                                                                                ? 'correctAnswerIndex'
                                                                                : `answers[${index}].isCorrect`
                                                                        }
                                                                        value={String(index)}
                                                                        checked={
                                                                            values.questionType === 'single' || values.questionType === 'truefalse'
                                                                                ? Number(values.correctAnswerIndex) === index
                                                                                : values.answers[index].isCorrect
                                                                        }
                                                                        className="form-check-input"
                                                                        onChange={(e) => {
                                                                            if (values.questionType === 'single' || values.questionType === 'truefalse') {
                                                                                setFieldValue('correctAnswerIndex', e.target.value);
                                                                            } else {
                                                                                setFieldValue(`answers[${index}].isCorrect`, e.target.checked);
                                                                            }
                                                                        }}
                                                                        aria-label={
                                                                            values.questionType === 'single' || values.questionType === 'truefalse'
                                                                                ? `Set option ${index + 1} as correct`
                                                                                : `Mark option ${index + 1} as correct`
                                                                        }
                                                                    />
                                                                </div>
                                                                {values.questionType === 'multiple' && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-danger ms-2 rounded-pill"
                                                                        onClick={() => remove(index)}
                                                                        aria-label={`Remove option ${index + 1}`}
                                                                    >
                                                                        <i className="isax isax-trash"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {touched.answers?.[index]?.text && errors.answers?.[index]?.text && (
                                                                <div className="text-danger">{errors.answers[index].text}</div>
                                                            )}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="text-muted">Select a question type to add answer options.</div>
                                                )}
                                                {values.questionType === 'multiple' && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-primary mt-2 rounded-pill"
                                                        onClick={() => push({ text: '', isCorrect: false })}
                                                        aria-label="Add option"
                                                    >
                                                        Add Option
                                                    </button>
                                                )}
                                                {touched.answers && errors.answers && typeof errors.answers === 'string' && (
                                                    <div className="text-danger">{errors.answers}</div>
                                                )}
                                                {touched.correctAnswerIndex && errors.correctAnswerIndex && (
                                                    <div className="text-danger">{errors.correctAnswerIndex}</div>
                                                )}
                                            </div>
                                        )}
                                    </FieldArray>

                                    <div className="modal-footer mt-3">
                                        <button
                                            type="button"
                                            className="btn bg-gray-100 rounded-pill"
                                            data-bs-dismiss="modal"
                                            onClick={closeModal}
                                            aria-label="Cancel"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-secondary rounded-pill"
                                            disabled={isSubmitting || !isValid}
                                            aria-label="Save changes"
                                        >
                                            Save Changes
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditQuestionModal;